import { fmtDate, money, num } from "../format";
import { CardSkeleton, ListingCard } from "./ListingCard";
import type { AssistantTurn, Turn } from "../useChat";
import type { Filters } from "../../../shared/types";

export function ThreadTurn({ turn, onOpen }: { turn: Turn; onOpen: (id: string) => void }) {
  if (turn.role === "user") {
    return (
      <div className="turn user">
        <div className="bubble">{turn.text}</div>
      </div>
    );
  }
  return <AssistantBlock turn={turn} onOpen={onOpen} />;
}

function AssistantBlock({ turn, onOpen }: { turn: AssistantTurn; onOpen: (id: string) => void }) {
  const pills = turn.filters ? describeFilters(turn.filters) : [];

  return (
    <div className="turn assistant">
      <div className="agent-line">
        <span className="agent-dot" />
        Loop
      </div>

      {turn.status === "searching" && (
        <div className="status">
          <span className="spinner" />
          {turn.intent === "answer" ? "Looking that up…" : "Searching 1,000 Toronto listings…"}
        </div>
      )}

      {pills.length > 0 && (
        <div className="filters">
          {pills.map((p) => (
            <span className="filter-pill" key={p}>
              {p}
            </span>
          ))}
        </div>
      )}

      {turn.status === "searching" && turn.intent === "search" ? (
        <div className="cards">
          {[0, 1, 2].map((i) => (
            <CardSkeleton index={i} key={i} />
          ))}
        </div>
      ) : (
        turn.listings && <Results turn={turn} onOpen={onOpen} />
      )}

      {turn.error ? (
        <div className="bubble error">{turn.error}</div>
      ) : (
        turn.text && (
          <div className="bubble">
            {turn.text}
            {turn.status === "streaming" && <span className="caret" />}
          </div>
        )
      )}
    </div>
  );
}

function Results({ turn, onOpen }: { turn: AssistantTurn; onOpen: (id: string) => void }) {
  const listings = turn.listings ?? [];
  if (listings.length === 0) return <div className="status">No listings matched.</div>;

  // With a thousand listings there is usually more than fits on screen; say so
  // rather than implying these are the only matches.
  const more = turn.totalExactMatches - turn.exactCount;

  return (
    <>
      <div className="results-head">
        <h2>
          {listings.length} {listings.length === 1 ? "place" : "places"} to look at
          {more > 0 && <span className="of-total"> of {turn.totalExactMatches} matches</span>}
        </h2>
        {turn.relaxed && (
          <span className="note">
            {turn.exactCount === 0
              ? "Nothing matched everything. Closest options"
              : `${turn.exactCount} exact ${turn.exactCount === 1 ? "match" : "matches"}, rest are close`}
          </span>
        )}
      </div>
      <div className="cards">
        {listings.map((l, i) => (
          <ListingCard listing={l} index={i} key={l.id} onOpen={onOpen} />
        ))}
      </div>
    </>
  );
}

/** Turn the filter object into the chips shown above the results. */
function describeFilters(f: Filters): string[] {
  const out: string[] = [];
  if (f.maxPrice != null && f.minPrice != null) out.push(`${money(f.minPrice)}–${money(f.maxPrice)}`);
  else if (f.maxPrice != null) out.push(`Under ${money(f.maxPrice)}`);
  else if (f.minPrice != null) out.push(`Over ${money(f.minPrice)}`);
  if (f.minBeds != null) out.push(f.minBeds === 0 ? "Studio" : `${f.minBeds}+ bed`);
  if (f.minBaths != null) out.push(`${f.minBaths}+ bath`);
  if (f.minSqft != null) out.push(`${num(f.minSqft)}+ sqft`);
  for (const n of f.neighborhoods ?? []) out.push(n);
  for (const t of f.propertyTypes ?? []) out.push(capitalize(t));
  for (const a of f.amenities ?? []) out.push(capitalize(a));
  if (f.petsAllowed) out.push("Pet friendly");
  if (f.furnished === true) out.push("Furnished");
  if (f.furnished === false) out.push("Unfurnished");
  if (f.parkingRequired) out.push("Parking");
  if (f.maxLeaseMonths != null) out.push(`≤ ${f.maxLeaseMonths}-month lease`);
  if (f.moveInBy) out.push(`By ${fmtDate(f.moveInBy)}`);
  if (f.sort === "price_asc") out.push("Cheapest first");
  if (f.sort === "price_desc") out.push("Priciest first");
  if (f.sort === "size_desc") out.push("Biggest first");
  return [...new Set(out)];
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
